import jsPDF from "jspdf";

export interface MagazineRow {
  conference_title?: string;
  region?: string;
  dates_and_location?: string;
  theme_and_scripture?: string;
  total_attendance?: number;
  total_adults?: number;
  total_youth?: number;
  total_children?: number;
  churches_represented?: number;
  narrative?: string;
  key_outcomes?: string;
  submitted_at?: string;
  status?: string;
}

const esc = (s: any) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\n/g, "<br/>");

export const buildMagazineHtml = (r: MagazineRow): string => `
<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>${esc(r.conference_title)}</title>
<style>
body{font-family:Calibri,Arial,sans-serif;color:#222;line-height:1.5;padding:24px;}
h1{color:#006B6B;border-bottom:2px solid #C9A84C;padding-bottom:6px;}
h2{color:#006B6B;margin-top:18px;}
table{border-collapse:collapse;width:100%;margin:10px 0;}
td,th{border:1px solid #ccc;padding:6px 10px;text-align:left;}
.muted{color:#666;font-size:12px;}
</style></head><body>
<h1>${esc(r.conference_title)}</h1>
<p class="muted"><strong>Region:</strong> ${esc(r.region)} &nbsp; | &nbsp;
<strong>Dates & Location:</strong> ${esc(r.dates_and_location)}</p>
<p><strong>Theme & Scripture:</strong> ${esc(r.theme_and_scripture)}</p>
<h2>Attendance</h2>
<table><tr>
<th>Total</th><th>Adults</th><th>Youth</th><th>Children</th><th>Churches</th>
</tr><tr>
<td>${r.total_attendance || 0}</td><td>${r.total_adults || 0}</td>
<td>${r.total_youth || 0}</td><td>${r.total_children || 0}</td>
<td>${r.churches_represented || 0}</td>
</tr></table>
<h2>Narrative</h2><p>${esc(r.narrative)}</p>
<h2>Key Outcomes</h2><p>${esc(r.key_outcomes)}</p>
${r.submitted_at ? `<p class="muted">Submitted: ${esc(r.submitted_at)}</p>` : ""}
</body></html>`;

const safeName = (s: string) =>
  (s || "report").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase();

export const downloadMagazineWord = (r: MagazineRow): { filename: string; base64: string } => {
  const html =
    `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">` +
    buildMagazineHtml(r) + `</html>`;
  const filename = `kit-magazine-${safeName(r.region || "")}-${safeName(r.conference_title || "")}.doc`;
  const blob = new Blob(["\ufeff", html], { type: "application/msword" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
  // base64 for email attachment
  const base64 = btoa(unescape(encodeURIComponent(html)));
  return { filename, base64 };
};

export const buildMagazinePdf = (r: MagazineRow): { filename: string; base64: string; doc: jsPDF } => {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const margin = 40;
  let y = margin;
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const writeLine = (text: string, opts: { size?: number; bold?: boolean; color?: [number, number, number] } = {}) => {
    doc.setFont("helvetica", opts.bold ? "bold" : "normal");
    doc.setFontSize(opts.size || 11);
    if (opts.color) doc.setTextColor(...opts.color); else doc.setTextColor(34, 34, 34);
    const lines = doc.splitTextToSize(text || "", pageW - margin * 2);
    for (const ln of lines) {
      if (y > pageH - margin) { doc.addPage(); y = margin; }
      doc.text(ln, margin, y);
      y += (opts.size || 11) * 1.35;
    }
  };
  writeLine(r.conference_title || "Conference Report", { size: 18, bold: true, color: [0, 107, 107] });
  y += 4;
  writeLine(`Region: ${r.region || ""}    Dates & Location: ${r.dates_and_location || ""}`, { size: 10, color: [100, 100, 100] });
  y += 6;
  writeLine(`Theme & Scripture: ${r.theme_and_scripture || ""}`, { bold: true });
  y += 6;
  writeLine("Attendance", { size: 14, bold: true, color: [0, 107, 107] });
  writeLine(
    `Total ${r.total_attendance || 0}  •  Adults ${r.total_adults || 0}  •  Youth ${r.total_youth || 0}  •  Children ${r.total_children || 0}  •  Churches ${r.churches_represented || 0}`
  );
  y += 6;
  writeLine("Narrative", { size: 14, bold: true, color: [0, 107, 107] });
  writeLine(r.narrative || "");
  y += 6;
  writeLine("Key Outcomes", { size: 14, bold: true, color: [0, 107, 107] });
  writeLine(r.key_outcomes || "");
  if (r.submitted_at) { y += 6; writeLine(`Submitted: ${r.submitted_at}`, { size: 9, color: [120, 120, 120] }); }

  const filename = `kit-magazine-${safeName(r.region || "")}-${safeName(r.conference_title || "")}.pdf`;
  const dataUri = doc.output("datauristring");
  const base64 = dataUri.split(",")[1] || "";
  return { filename, base64, doc };
};

export const downloadMagazinePdf = (r: MagazineRow) => {
  const { filename, doc } = buildMagazinePdf(r);
  doc.save(filename);
};
